import {Button, closeCurrentDialog} from 'platform/components';
import {Box, Heading, HStack, Icon, Show, Text, VStack} from 'platform/foundation';

import {AdvertisementPreflightIssue} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {VehiclePhotosRoutes} from '@dms/teas';

import {composePath, useNavigate} from 'shared/web';

import {getAdvertisingPreflightIssueText} from '../utils/getAdvertisingPreflightIssueText';
import {groupAdvertisingPreflightIssues} from '../utils/groupAdvertisingPreflightIssues';

interface PlatformPreflightDialogProps {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  platformTitle: string;
  issues: AdvertisementPreflightIssue[];
  canEditVehicle: boolean;
  canEditPlatform: boolean;
  canReadPhotos: boolean;
  onOpenPlatformDetail: () => void;
}

export function PlatformPreflightDialog(props: PlatformPreflightDialogProps) {
  const navigate = useNavigate();
  const openPlatform = () => {
    closeCurrentDialog();
    props.onOpenPlatformDetail();
  };
  const goTo = (path: string) => {
    closeCurrentDialog();
    navigate(path);
  };
  const titles = {
    vehicle: i18n.t('entity.vehicle.labels.vehicleData'),
    prices: i18n.t('entity.vehicle.labels.carPrices'),
    photos: i18n.t('entity.photo.labels.photos'),
    platform: props.platformTitle,
    other: i18n.t('general.labels.details'),
  };

  const hasOnlyMissingFields =
    props.issues.length > 0 &&
    props.issues.every(
      (issue) => issue.kind === 'field' && issue.reasonCode === 'required-source-missing'
    );

  return (
    <Box padding={4}>
      <VStack spacing={4} align="center">
        <Icon value="alert_icon/circle" size={20} isNotFilled />
        <Heading size={3}>
          {hasOnlyMissingFields
            ? i18n.t('entity.mandatoryFields.errorModal.heading')
            : i18n.t('general.labels.blocked')}
        </Heading>
        <Show when={hasOnlyMissingFields}>
          <Text align="center" color="secondary">
            {i18n.t('entity.mandatoryFields.errorModal.description')}
          </Text>
        </Show>
        <Box paddingVertical={6} maxWidth={100} width="100%">
          <VStack spacing={6}>
            {groupAdvertisingPreflightIssues(props.issues).map((group) => (
              <VStack
                key={`${group.kind}:${group.field?.href ?? ''}:${group.field?.tab ?? ''}`}
                spacing={3}
                align="flex-start"
              >
                <Text alternative>
                  {group.kind === 'vehicle' && !group.field?.tab
                    ? i18n.t('entity.vehicle.labels.generalInformation')
                    : titles[group.kind]}
                </Text>
                <Box
                  width="100%"
                  borderColor="palettes.red.70.100"
                  border="1px solid"
                  backgroundColor="palettes.red.10.100"
                  borderRadius="medium"
                  padding={4}
                >
                  <HStack spacing={4} align="flex-start">
                    <Icon color="palettes.red.70.100" value="alert/error" size={5} />
                    <VStack spacing={2} role="list">
                      {[...new Set(group.issues.map(getAdvertisingPreflightIssueText))].map(
                        (text) => (
                          <Text key={text} role="listitem" size="xSmall" color="danger">
                            {text}
                          </Text>
                        )
                      )}
                    </VStack>
                  </HStack>
                </Box>
                <HStack spacing={3}>
                  <Show when={group.kind === 'vehicle' && !!group.field}>
                    <Button
                      variant="primary"
                      title={
                        group.field?.href === vehiclesRoutes.conditionsInspections
                          ? i18n.t('module.inspection.title')
                          : i18n.t('entity.vehicle.actions.goToEditVehicle')
                      }
                      isDisabled={!props.canEditVehicle}
                      onClick={() => {
                        if (group.field?.href === vehiclesRoutes.conditionsInspections) {
                          return goTo(
                            composePath(vehiclesRoutes.conditionsInspections, {
                              params: {id: props.vehicleId},
                            })
                          );
                        }
                        return goTo(
                          composePath(
                            group.field?.tab ? vehiclesRoutes.editV2Tab : vehiclesRoutes.editV2,
                            {
                              params: {id: props.vehicleId, tab: group.field?.tab},
                            }
                          )
                        );
                      }}
                    />
                  </Show>
                  <Show when={group.kind === 'photos'}>
                    <Button
                      variant="primary"
                      title={i18n.t('entity.photo.actions.editImages')}
                      isDisabled={!props.canReadPhotos}
                      onClick={() =>
                        goTo(
                          composePath(vehiclesRoutes.photos, {
                            params: {id: props.vehicleId, tab: VehiclePhotosRoutes.vehiclePhotos},
                          })
                        )
                      }
                    />
                  </Show>
                  <Show when={group.kind === 'prices'}>
                    <Button
                      variant="primary"
                      title={i18n.t('general.actions.goToDetail')}
                      onClick={() =>
                        goTo(
                          composePath(vehiclesRoutes.financesPrices, {
                            params: {id: props.vehicleId},
                          })
                        )
                      }
                    />
                  </Show>
                  <Show when={group.kind === 'platform'}>
                    <Button
                      variant="primary"
                      title={`${i18n.t('page.advertisement.actions.editPlatform')} ${props.platformTitle}`}
                      isDisabled={!props.canEditPlatform}
                      onClick={openPlatform}
                    />
                  </Show>
                </HStack>
              </VStack>
            ))}
          </VStack>
        </Box>
        <Box paddingTop={4}>
          <Button
            variant="secondary"
            title={i18n.t('general.actions.back')}
            onClick={closeCurrentDialog}
          />
        </Box>
      </VStack>
    </Box>
  );
}
